import { countriesFromLocale } from "../../../utils/countries";
import { useGetEnumsQuery } from "../../../newapi/global/generalApiSlice";
import { useGetInvoiceCategorySelectQuery } from "../../../newapi/financial/invoiceCategorySlice";
import { useTranslation } from 'react-i18next';
import { useGetRelationsSelectQuery } from "../../../newapi/global/relation/relationSlice";
import { useGetShippersSelectQuery, useGetShipperProfilesSelectQuery } from "../../../newapi/shipper/shipperSlice";
import { useGetAllShipperProfilesSelectQuery } from "../../../newapi/shipper/shipperprofileSlice";
import { useGetWarehouseZonesSelectQuery } from "../../../newapi/warehouse/warehouseApiSlice";
import { useGetCarriersSelectQuery } from "../../../newapi/carrier/carrierSlice";


export const DND_STREAM_RULE = "STREAM_RULE";
export const DND_STREAM_RULE_LINE = "STREAM_RULE_LINE";

export const getCountries = (language) => countriesFromLocale(language);

export const dropdownKeys = {
    destination: 'destination'
}

export const conditionKeys = [
    'colli',
    'weight',
    'length',
    'width',
    'height',
    'volume',
    'time'
]

export const condtionLineKeys = [
    ...conditionKeys,
    ...Object.values(dropdownKeys),
]

export const getConditionKey = (key) => {
    if(condtionLineKeys.includes(key)) {
        return key;
    }
    return "choose_key";
}

export const conditions = (key) => {
    if(conditionKeys.includes(key)) {
        return [
            'equalto',
            'notequalto',
            'greaterthan',
            'smallerthan',
            'greaterthanorequalto',
            'smallerthanorequalto',
        ]
    }
    return ['equalto','notequalto'];
}

export const getDisplayValue = (condition, countries) => {
    switch(condition.key) {
        case "deliverycountry": return countries.find(country => country.alpha2 === condition.value)?.name;
        default: return condition.value;
    }
}

// Custom hook for centralized data fetching
export const useDropDownFetchData = (shipperId = null) => {
    const { data: deliveryMethods, isLoading: enumsLoading, error: enumsError } = useGetEnumsQuery("order_delivery_method");
    const { data: invoiceCategories, isLoading: categoriesLoading, error: categoriesError } = useGetInvoiceCategorySelectQuery();
    const { data: relations, isLoading: relationsLoading, error: relationsError } = useGetRelationsSelectQuery();
    const { data: warehouseZones, isLoading: warehouseZonesLoading, error: warehouseZonesError } = useGetWarehouseZonesSelectQuery();
    const { data: shippers, isLoading: shippersLoading, error: shipperError } = useGetShippersSelectQuery();
    const { data: carriers, isLoading: carriersLoading, error: carrierError } = useGetCarriersSelectQuery();
    const { data: shipperProfilesFromShipper, isLoading: profilesFromShipperLoading, error: profilesFromShipperError } = useGetShipperProfilesSelectQuery(shipperId, { skip: !shipperId });
    const { data: shipperProfilesFromAll, isLoading: profilesFromAllLoading, error: profilesFromAllError } = useGetAllShipperProfilesSelectQuery(undefined, { skip: !!shipperId });

    const {t} = useTranslation();

    // Combined loading state - include profiles loading only if shipperId is provided
    const isLoading = enumsLoading || categoriesLoading || relationsLoading || (shipperId ? profilesFromShipperLoading : profilesFromAllLoading) || warehouseZonesLoading || carriersLoading || shippersLoading;
    
    // Combined error state - include profiles error only if shipperId is provided
    const error = enumsError || categoriesError || relationsError || shipperError || carrierError || (shipperId ? profilesFromShipperError : profilesFromAllError) || warehouseZonesError;
    
    // Only return data when ALL requests are complete
    const allDataReady = deliveryMethods && invoiceCategories && relations && warehouseZones && shippers && carriers && (shipperId ? shipperProfilesFromShipper : shipperProfilesFromAll);
    
    // Return combined data
    return {
        data: allDataReady ? {
            deliveryMethods: deliveryMethods["order.delivery.method"].map(x => { return {id: x, name: t(x)}}),
            invoiceCategories,
            relations,
            shippers,
            carriers,
            shipperProfiles: shipperId ? shipperProfilesFromShipper : shipperProfilesFromAll,
            warehouseZones: warehouseZones.data.map(x => { return {
                id: x.id,
                name: `${x.description} (${x.warehouse.name})`
            }})
        } : null,
        isLoading,
        error,
        allDataReady
    };
};

// Pure async function - waits for ALL data before returning
export const getDropDownFetchDataAsync = async (dispatch) => {
    try {
        // Import your API slices
        const { generalApiSlice } = await import('../../../newapi/global/generalApiSlice');
        const { invoiceCategorySlice } = await import('../../../newapi/financial/invoiceCategorySlice');
        const { invoiceProductSlice } = await import('../../../newapi/financial/invoiceProductSlice');
        
        // Dispatch all queries and wait for ALL to complete
        const [enumsResult, productsResult, categoriesResult] = await Promise.all([
            dispatch(generalApiSlice.endpoints.getEnums.initiate("cost_type,cost_invoice_type")).unwrap(),
            dispatch(invoiceProductSlice.endpoints.getInvoiceProductsSelect.initiate()).unwrap(),
            dispatch(invoiceCategorySlice.endpoints.getInvoiceCategorySelect.initiate()).unwrap()
        ]);
        
        // Return combined object only when ALL data is ready
        return {
            enums: enumsResult,
            invoiceProducts: productsResult,
            invoiceCategories: categoriesResult
        };
    } catch (error) {
        console.error('Failed to fetch dropdown data:', error);
        // throw error;
    }
};

// Simpler version if you have access to store directly
export const getDropDownFetchDataDirect = async () => {
    try {
        // Import store
        const { store } = await import('../../../store');
        const { generalApiSlice } = await import('../../../newapi/global/generalApiSlice');
        const { invoiceCategorySlice } = await import('../../../newapi/financial/invoiceCategorySlice');
        const { invoiceProductSlice } = await import('../../../newapi/financial/invoiceProductSlice');
        
        // Dispatch all queries and wait for ALL to complete
        const [enumsResult, productsResult, categoriesResult] = await Promise.all([
            store.dispatch(generalApiSlice.endpoints.getEnums.initiate("cost_type,cost_invoice_type")).unwrap(),
            store.dispatch(invoiceProductSlice.endpoints.getInvoiceProductsSelect.initiate()).unwrap(),
            store.dispatch(invoiceCategorySlice.endpoints.getInvoiceCategorySelect.initiate()).unwrap()
        ]);
        
        // Return combined object only when ALL data is ready
        return {
            enums: enumsResult,
            invoiceProducts: productsResult,
            invoiceCategories: categoriesResult
        };
    } catch (error) {
        console.error('Failed to fetch dropdown data:', error);
        throw error;
    }
};

export const getMobileCondition = (condition) => {
    switch (condition) {
        case "greaterthan" : return ">";
        case "smallerthan" : return "<";
        case "equalto" : return "=";
        case "notequalto" : return "!=";
        case "greaterthanorequalto" : return ">=";
        case "smallerthanorequalto" : return "<=";
        case "isbefore": return "<"; 
        case "isafterorequalto": return ">="; 
        case "allproductsat": return "@";
        case "anyproductat": return "#";  
        case "isinselection": return "^"; 
        case "isnotinselection": return "!^"; 
        case "is": return "=";  
        case "isnot": return "!="; 
        case "contains": return "~";  
        case "doesnotcontain": return "!~"; 
        case "startswith": return "<<";  
        case "doesnotstartswith": return "!<<";  
        case "endswith": return ">>";  
        case "doesnotendwith": return "!>>";  
        default: return "?";  
    }
}


export const textValueTypes = ["string","text", "time", "date", "text_is"];
export const numberValueTypes = ["number"];
export const booleanValueTypes = ["boolean"];
export const dropdownValueTypes = [
    "country_list",
    "day_with_additional_list",
    "relation_list",
    "warehouse_list",
    "warehouse_location_list",
    "invoice_category_list",
    "delivery_method_list",
    "integrator_list",
    "shipper_profile_list",
    "return_profile_list",
    "warehousezone_list",
    "shipper_list",
    "carrier_list",
    "true_not_true_list",
    "pickup_point_carrier_list",
    "list"
];

export const searchableListValueTypes = [
    "product_list"
];

export const getListOptions = (valueType, objects, t, getObjects = false, listValues = []) => {
    if (getObjects) {
        switch(valueType) {
            case "carrier_list":
                objects = objects.listOptions?.carriers;
                break;
            case "country_list":
                objects = objects.countries;
                break;
            case "delivery_method_list":
                objects = objects.listOptions?.deliveryMethods;
                break;
            case "invoice_category_list":
                objects = objects.listOptions?.invoiceCategories;
                break;
            case "list":
                objects = listValues?.map((item, index) => ({ id: index, name: item })) ?? [];
                break;
            case "relation_list":
                objects = objects.listOptions?.relations;
                break;
            case "shipper_list":
                objects = objects.listOptions?.shippers;
                break;
            case "shipper_profile_list":
                objects = objects.listOptions?.shipperProfiles;
                break;
            case "return_profile_list":
                objects = objects.listOptions?.returnProfiles;
                break;
            case "warehousezone_list":
                objects = objects.listOptions?.warehouseZones;
                break;
            default:
                objects = objects.countries;
         }
    }

    switch(valueType) {
        case "carrier_list":
            return objects;
        case "country_list":
            return objects;
        case "day_with_additional_list":
            return [{
                id: 1,
                name: t("monday")
            },
            {
                id: 2,
                name: t("tuesday")
            },
            {
                id: 3,
                name: t("wednesday")
            },
            {
                id: 4,
                name: t("thursday")
            },
            {
                id: 5,
                name: t("friday")
            },
            {
                id: 6,
                name: t("saturday")
            },
            {
                id: 0,
                name: t("sunday")
            },
            {
                id: "workingdays",
                name: t("workingdays")
            },
            {
                id: "weekend",
                name: t("weekend")
            }
        ];
        case "delivery_method_list":
            return objects;
        case "list":
            return listValues?.map((item, index) => ({ id: index, name: item })) ?? [];
        case "relation_list":
            return objects;
        case "warehouse_list":
            return [];
        case "warehousezone_list":
            return objects;
        case "invoice_category_list":
            return objects;
        case "shipper_list":
            return objects;
        case "shipper_profile_list":
            return objects;
        case "return_profile_list":
            return objects;
        case "pickup_point_carrier_list":
            return [{id: "dhl", name: "DHL"}, {id: "postnl", name: "PostNL"}]
        case "integrator_list":
            return [
                {
                    id: "etailize",
                    name: "E-tailize"
                }
            ];
        case "true_not_true_list":
            return [
                {
                    id: "true",
                    name: t("true")
                }, {
                    id: "nottrue",
                    name: t("nottrue")
                }
            ]
        default:
            return [];
    }
}